/** Gates the v2 admin shell (`admin-platform-v2/`). When off, `/admin` loads legacy `admin-platform/`. */
export const ADMIN_PLATFORM_FEATURE_FLAG = 'enable_admin_platform';

export const ADMIN_BASE_PATH = '/admin';

/** Team slugs — first segment under /admin (matches megamenu roots). */
export const ADMIN_TEAM_SLUG = {
    Platform: 'platform',
    BrandData: 'brand-data'
} as const;

export type AdminTeamSlug = (typeof ADMIN_TEAM_SLUG)[keyof typeof ADMIN_TEAM_SLUG];

export const ADMIN_HOME_LEGACY = '/dashboard';
export const ADMIN_HOME_PLATFORM_USERS = '/admin/platform/users';

/** @deprecated Legacy URL prefixes — redirects only. */
export const ADMIN_LEGACY_PATH_PREFIX = {
    OrderArea: 'order-area-admin',
    BrandData: 'brand-data-admin',
    OrderManagement: 'order-management',
    Content: 'content'
} as const;

/** Absolute routerLink array for navigation from any /admin child route. */
export function buildAdminPath(...segments: string[]): string[] {
    return ['/', 'admin', ...segments];
}

export function buildPlatformPath(...appSegments: string[]): string[] {
    return buildAdminPath(ADMIN_TEAM_SLUG.Platform, ...appSegments);
}

export function buildBrandDataPath(...appSegments: string[]): string[] {
    return buildAdminPath(ADMIN_TEAM_SLUG.BrandData, ...appSegments);
}

/** Legacy admin shell paths (`/admin/brand-data-admin/...`). */
export function buildBrandDataLegacyPath(...appSegments: string[]): string[] {
    return buildAdminPath(ADMIN_LEGACY_PATH_PREFIX.BrandData, ...appSegments);
}

/** Legacy admin shell paths (`/admin/order-area-admin/...`). */
export function buildOrderAreaLegacyPath(...appSegments: string[]): string[] {
    return buildAdminPath(ADMIN_LEGACY_PATH_PREFIX.OrderArea, ...appSegments);
}

/** @deprecated Use buildBrandDataPath — kept for existing PDI route constant files. */
export function buildBrandDataPdiPath(...segments: string[]): string[] {
    return buildBrandDataPath(...segments);
}

/** Maps legacy / mistaken URL shapes onto /admin/{team}/{app}. */
export function normalizeAdminUrl(url: string): string {
    return url
        .replace(/^\/admin\/crm\/users/, '/admin/platform/users')
        .replace(/^\/admin\/locations\/akas/, '/admin/platform/akas')
        .replace(/^\/admin\/locations\/exids/, '/admin/platform/exids')
        .replace(/^\/admin\/locations(\/|$)/, '/admin/platform$1')
        .replace(/^\/admin\/product-data-import\//, '/admin/brand-data/')
        .replace(/^\/admin\/brand-data\/product-data-import\//, '/admin/brand-data/')
        .replace(/^\/admin\/order-management\//, '/admin/platform/')
        .replace(/^\/admin\/content\//, '/admin/brand-data/');
}

export function adminPathToUrl(routerLink: readonly string[]): string {
    if (routerLink.length === 1 && routerLink[0].startsWith('/')) {
        return normalizeAdminUrl(routerLink[0]);
    }

    const segments = routerLink[0] === '/' ? routerLink.slice(1) : routerLink;
    return normalizeAdminUrl(`/${segments.join('/')}`);
}

/** Absolute routerLink array — safe for navigation from any /admin route. */
export function normalizeAdminRouterLink(routerLink: readonly string[]): string[] {
    const segments = adminPathToUrl(routerLink).split('/').filter(Boolean);
    return ['/', ...segments];
}
